import * as React from 'react';
import { Box, Flex, Select, SelectProps } from '@chakra-ui/react';
import { IOption } from '../../../types';
import { withConfirm } from '../withConfirm';
import { Input } from '../Input';
import { UndoEdit } from '../../iconButtons/UndoEdit';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

interface IProps extends DirectionProp {
  value?: string | number;
  id?: string;
  name?: string;
  otherValue?: string | number;
  placeHolder?: string;
  options?: IOption[];
  otherOptionLabel?: string;
  otherOptions?: IOption[];
  label?: string;
  subText?: string;
  inputHelperText?: string;
  errorText?: string;
  forwardedRef?: ((instance: HTMLSelectElement | null) => void) | React.RefObject<HTMLSelectElement> | null | undefined;
  onChange?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onFocus?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onChangeOtherText?: (event: React.FormEvent<HTMLInputElement>) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  size?: 'lg' | 'md' | 'sm';
  mb?: number | string;
  selectProps?: SelectProps;
}

const SelectWithOther: React.FC<IProps> = (props: IProps) => {
  const {
    value,
    id,
    name,
    otherValue,
    placeHolder = 'Please choose an option…',
    options,
    otherOptionLabel,
    otherOptions,
    label,
    subText,
    errorText,
    forwardedRef,
    onChange,
    onBlur,
    onFocus,
    onChangeOtherText,
    canUndo = false,
    onUndo,
    size = 'lg',
    mb = 0,
    direction,
    selectProps
  } = props;

  const renderOptions = () => {
    if (!options) {
      return [];
    }
    const renderedOptions = [];

    options.forEach((option, key) => {
      if (option.populate) {
        renderedOptions.push(
          <optgroup label={option.label} key={key}>
            {option.populate.options.map((option, index) => {
              return (
                <option key={`${key}-${index}`} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </optgroup>
        );
      } else {
        renderedOptions.push(
          <option key={key} value={option.value}>
            {option.label}
          </option>
        );
      }
    });

    otherOptions &&
      otherOptions.forEach((option: IOption, index: number) => {
        renderedOptions.push(
          <option key={`other-${index}`} value={option ? option.value : 'other'}>
            {option ? option.label : 'Other'}
          </option>
        );
      });

    return renderedOptions;
  };

  const otherOptionIndex = otherOptions ? otherOptions.findIndex(option => option.value === value) : -1;

  const onSelect = React.useCallback(
    (event: React.FormEvent<HTMLSelectElement>) => {
      // ignore placeholder option
      if (onChange && event.currentTarget.value !== '') onChange(event);
    },
    [onChange]
  );

  return (
    <CustomFormControl
      id={id}
      controlProps={{
        isInvalid: errorText && otherOptionIndex === -1 ? true : false,
        mb: mb
      }}
      direction={direction}
      size={size}
      label={label}
      labelProps={{ htmlFor: `${id}-input-field` }}
      subLabel={subText}
      error={errorText}
    >
      <Flex gap={{ base: 1, lg: 2 }}>
        <Box flexGrow="1">
          <Select
            id={`${id}-input-field`}
            name={name}
            data-selector={`${id}-input-field`}
            aria-label={label ? null : label}
            onChange={onSelect}
            value={value}
            ref={forwardedRef}
            isInvalid={
              (otherOptions ? otherOptions.findIndex(option => option.value === value) > -1 : value === 'other')
                ? false
                : !!errorText
            }
            onBlur={onBlur}
            onFocus={onFocus}
            size={size}
            placeholder={placeHolder}
            {...selectProps}
          >
            {renderOptions()}
          </Select>

          {(otherOptions && otherOptions.length && otherOptionIndex > -1) || value === 'other' ? (
            <Box mt={6}>
              <Input
                id={`${id}_other`}
                name={`${name}_other`}
                label={otherOptionLabel}
                onChange={onChangeOtherText}
                errorText={errorText}
                value={otherValue}
              />
            </Box>
          ) : null}
        </Box>

        {canUndo ? <UndoEdit id={id} onUndo={onUndo} /> : null}
      </Flex>
    </CustomFormControl>
  );
};

const SelectWithOtherWithConfirm = withConfirm(SelectWithOther);

export { SelectWithOther, SelectWithOtherWithConfirm };
